import { useEffect, useState } from "react";
import { ClioService } from "../../../api";
import { LitigationFunnelMatter } from "../../../api/models/LitigationFunnelMatter";
import { Areas, LitigationArea } from "./Areas";
import { Area } from "./Area";
import { Box, Button, MenuItem, TextField } from "@mui/material";
import { LitigationReportRequest } from "../../../api/models/LitigationReportRequest";
import { MatterData } from "../../../api/models/MatterData";
import { CustomFieldOption } from "../../../api/models/CustomField";

type Props = {
    areas: LitigationArea[];
    matters: LitigationFunnelMatter[];
    matterOptions: MatterData[];
    loadReport: (filter: LitigationReportRequest) => void;
}

export default function StagesReport({
    areas,
    matters,
    matterOptions,
    loadReport
}: Props) {
    const [reportFilter, setReportFilter] = useState<LitigationReportRequest>({})
    
    const [assignedParalegals, setAssignedParalegals] = useState<CustomFieldOption[]>([]);
    const [responsibleAttorneys, setResponsibleAttorneys] = useState<CustomFieldOption[]>([]);
    const [seniorManagingAttorneys, setSeniorManagingAttornyes] = useState<CustomFieldOption[]>([]);
    const [supportingAttorneys, setSupportingAttorneys] = useState<CustomFieldOption[]>([]);

    useEffect(() => {
        loadCustomFieldOptions("Assigned Paralegal (AP)", setAssignedParalegals);
        loadCustomFieldOptions("Senior Managing Atty (SMA)", setSeniorManagingAttornyes);
        loadCustomFieldOptions("Supporting Atty (SA)", setSupportingAttorneys);
        loadCustomFieldOptions("Responsible Associate Atty (RAA)", setResponsibleAttorneys);
    }, [])

    const loadCustomFieldOptions = async (
        fieldName: string,
        setter: (options: CustomFieldOption[]) => void
        ): Promise<void> =>  {
        try {
            const customFields = await ClioService.getApiClioCustomFields(fieldName);
            const options = customFields.find(c => c.name === fieldName)?.picklist_options ?? [];
            setter(options.sort((a, b) => a.option.localeCompare(b.option)));
        } catch (error) {
            console.error(`Failed to load options for ${fieldName}`, error);
            setter([]);
        }
    }

    const handleLoadReport = () => {
        loadReport(reportFilter);
    }

    return (<>
        <Box sx={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column', gap: 2, paddingTop: 2 }}>
            <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                <TextField
                    label="Matter"
                    select
                    value={reportFilter.matterId ?? ""}
                    onChange={(e) => setReportFilter(prev => ({
                        ...prev,
                        matterId: e.target.value === "" ? undefined : Number(e.target.value)
                    }))}
                    size="small"
                    sx={{ minWidth: 150 }}
                >
                    <MenuItem value="">All</MenuItem>
                    {matterOptions.map(matter => (
                        <MenuItem key={matter.id} value={matter.id}>{matter.display_number}</MenuItem>
                    ))}
                </TextField>

                <TextField
                    label="RAA"
                    select
                    value={reportFilter.raa ?? ""}
                    onChange={(e) => setReportFilter(prev => ({
                        ...prev,
                        raa: e.target.value === "" ? undefined : Number(e.target.value)
                    }))}
                    size="small"
                    sx={{ minWidth: 150 }}
                >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="0">None</MenuItem>
                    {responsibleAttorneys.map(atty => (
                        <MenuItem key={atty.id} value={atty.id}>{atty.option}</MenuItem>
                    ))}
                </TextField>

                <TextField
                    label="SMA"
                    select
                    value={reportFilter.sma ?? ""}
                    onChange={(e) => setReportFilter(prev => ({
                        ...prev,
                        sma: e.target.value === "" ? undefined : Number(e.target.value)
                    }))}
                    size="small"
                    sx={{ minWidth: 150 }}
                >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="0">None</MenuItem>
                    {seniorManagingAttorneys.map(atty => (
                        <MenuItem key={atty.id} value={atty.id}>{atty.option}</MenuItem>
                    ))}
                </TextField>

                <TextField
                    label="SA"
                    select
                    value={reportFilter.sa ?? ""}
                    onChange={(e) => setReportFilter(prev => ({
                        ...prev,
                        sa: e.target.value === "" ? undefined : Number(e.target.value)
                    }))}
                    size="small"
                    sx={{ minWidth: 150 }}
                >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="0">None</MenuItem>
                    {supportingAttorneys.map(atty => (
                        <MenuItem key={atty.id} value={atty.id}>{atty.option}</MenuItem>
                    ))}
                </TextField>

                <TextField
                    label="AP"
                    select
                    value={reportFilter.ap ?? ""}
                    onChange={(e) => setReportFilter(prev => ({
                        ...prev,
                        ap: e.target.value === "" ? undefined : Number(e.target.value)
                    }))}
                    size="small"
                    sx={{ minWidth: 150 }}
                >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="0">None</MenuItem>
                    {assignedParalegals.map(atty => (
                        <MenuItem key={atty.id} value={atty.id}>{atty.option}</MenuItem>
                    ))}
                </TextField>
                
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleLoadReport}
                >
                    Load Report
                </Button>
            </Box>
            
            <Box 
                sx={{
                    display: 'flex',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    gap: 4,
                    flexWrap: 'nowrap',
                    flex: 1,
                    padding: 1,
                }}>
                {areas.map(area => (
                    <Area
                        key={area.area}
                        area={area}
                        matters={matters.filter(m => m.area === area.area)}
                    />
                ))}
            </Box>
        </Box>
    </>);
}
